export interface BaseDto {
  id: number;
  createTimestamp: number;
  updateTimestamp?: number | null;
  createdUserId: string;
  updateUserId?: string | null;
  /**
   * Translated field values for the current request locale.
   * Key = field name (e.g. "city", "categoryName"), value = translated text.
   * Null when the locale is English or the cache is cold (first request).
   * Usage: display = dto.localized?.city ?? dto.city
   */
  localized?: Record<string, string> | null;
}

// Operation types
type NormalOperationType =
  | "LIKE"
  | "EQUALS"
  | "NOT"
  | "IN"
  | "NOT_IN"
  | "GT"
  | "LT"
  | "GTE"
  | "LTE"
  | "IS_NULL"
  | "IS_NOT_NULL"
  | "ENDS_WITH"
  | "START_WITH"
  | "BETWEEN"
  | "DOES_NOT_EQUALS"
  | "DOES_NOT_CONTAINS";

type ListOperationType =
  | "CONTAINS"
  | "NOT_CONTAINS"
  | "CONTAINS_LIKE"
  | "CONTAINS_ALL"
  | "CONTAINS_ANY"
  | "IS_EMPTY"
  | "IS_NOT_EMPTY"
  | "SIZE_EQUALS"
  | "SIZE_GT"
  | "SIZE_LT"
  | "DOES_NOT_EQUALS"
  | "DOES_NOT_CONTAINS";

// Base interface for common properties
interface BaseFilterCriterion {
  key: string;
  value: any;
  joinTable?: string | null;
}

// Normal filter criterion for regular fields and enums
export interface NormalFilterCriterion extends BaseFilterCriterion {
  fieldType?: "NORMAL" | "ENUM";
  operation: NormalOperationType;
}

// List filter criterion for element collections
export interface ListFilterCriterion extends BaseFilterCriterion {
  fieldType: "ELEMENT_COLLECTION";
  operation: ListOperationType;
}

// Discriminated union type
export type FilterCriterion = NormalFilterCriterion | ListFilterCriterion;

export interface FilterGroup {
  operator: "AND" | "OR";
  criteria: FilterCriterion[];
  groups?: FilterGroup[];
}

// Example usage and type-safe builders
export const NormalFilter = (
  key: string,
  operation: NormalOperationType,
  value: any,
  joinTable?: string
): NormalFilterCriterion => ({
  key,
  operation,
  value,
  joinTable,
  fieldType: "NORMAL", // optional, but explicit
});

export const EnumFilter = (
  key: string,
  operation: NormalOperationType,
  value: any,
  joinTable?: string
): NormalFilterCriterion => ({
  key,
  operation,
  value,
  joinTable,
  fieldType: "ENUM",
});

export const ListFilter = (
  key: string,
  operation: ListOperationType,
  value: any,
  joinTable?: string
): ListFilterCriterion => ({
  key,
  operation,
  value,
  joinTable,
  fieldType: "ELEMENT_COLLECTION",
});
export const slugify = (text: string) =>
  text
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const unslugify = (slug: string) =>
  slug
    .replace(/-and-/g, " & ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
